import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';
import { useDrag, useDrop } from 'react-dnd';
import { getElementOffsetPerOrientation } from 'paper/utils/helpers';
import FeatureFlags from 'util/featureFlags';
import { detectTouchScreen } from '../../../../util/detectBrowser';
import Loader from '../../../../components/common/ui/Loader';

// Components
import ImageUploader from '../../common/ImageUploader';
import { RecoloredImage } from './RecoloredImage';

// Actions
import { actions as projectActions } from '../../../actions/projectActions';

// Utilities
import { getImageUrl } from '../../../util/imageHelper';
import { ptToPx } from '../../../util';

// Styles
import './ImageLayer.less';

function ImageLayer({
  allowPlaceholder,
  controlsEnabled,
  customizationDeleted,
  hasUploadcareImage,
  imageElement,
  linkedElement,
  isActive,
  isFlipped,
  isFoldable,
  onImageDelete,
  onImageSave,
  onImageReorder,
  pageHeight,
  pageWidth,
  setActiveField,
  pageOrientation,
}) {
  const [extraCustomizationEnabled] = useState(FeatureFlags.get('cardsExtraCustomization'));
  const [xOffset, yOffset] = getElementOffsetPerOrientation(
    imageElement.y,
    imageElement.x,
    pageHeight,
    pageWidth,
    isFoldable,
    pageOrientation
  );

  const contentKey = imageElement.uuid;
  const [isBusy, setIsBusy] = useState(false);

  const imageURL = useMemo(() => {
    return imageElement?.image_id ? getImageUrl(imageElement.image_id) : null;
  }, [imageElement?.image_id]);

  // Whenever image_id changes, the image is done loading. Set isBusy to false.
  useEffect(() => {
    setIsBusy(false);
  }, [imageElement.image_id]);

  const handleClick = () => {
    if (controlsEnabled && !isActive && !customizationDeleted) {
      setActiveField(contentKey);
    }
  };

  const handleUploadComplete = useCallback(
    imageIdsByKey => {
      onImageSave(imageIdsByKey);
      setActiveField(null);
    },
    [onImageSave, setActiveField]
  );

  const handleDelete = useCallback(() => {
    onImageDelete(contentKey);
    setActiveField(null);
  }, [onImageDelete, setActiveField, contentKey]);

  const [{ isDragging }, dragRef, previewRef] = useDrag(
    () => ({
      type: 'REORDERED_IMAGE',
      item: { contentKey, imageElement },
      end: (item, monitor) => {
        // If this layer is dropped onto another, set isBusy to true while they both update
        const { droppedContentKey } = monitor.getDropResult() || {};
        if (!!droppedContentKey && droppedContentKey !== contentKey) {
          setIsBusy(true);
        }
      },
      collect: monitor => ({ isDragging: monitor.isDragging() }),
    }),
    [contentKey, imageElement]
  );

  // When isFlipped, turn the image upside down as if you were seeing it folded from the back
  const imageStyles = useMemo(() => (isFlipped ? { transform: 'rotate(180deg)' } : undefined), [
    isFlipped,
  ]);

  const [{ canDrop, isOver }, dropRef] = useDrop(
    () => ({
      accept: ['REORDERED_IMAGE'],
      drop: item => {
        if (item.contentKey === contentKey) return null; // Don't do anything if dropped on self
        onImageReorder(imageElement, item.imageElement);
        setIsBusy(true);
        return { droppedContentKey: contentKey };
      },
      collect: monitor => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
    }),
    [contentKey, imageElement, onImageReorder]
  );

  if (!imageElement.image_id && !allowPlaceholder) return null;

  const shouldColorizeImage =
    extraCustomizationEnabled &&
    !!linkedElement?.default_font_color &&
    linkedElement?.default_font_color !== linkedElement?.font_color;
  const width = Math.round(ptToPx(imageElement.width));
  const height = Math.round(ptToPx(imageElement.height));

  return (
    <div
      className={cx('image-layer-container', { isActive, isBusy, isEditable: controlsEnabled })}
      style={{
        left: `${Math.round(ptToPx(xOffset))}px`,
        top: `${Math.round(ptToPx(yOffset))}px`,
        width: `${width}px`,
        height: `${height}px`,
      }}
      onClick={handleClick}
      onDrop={e => e.preventDefault()}
      ref={dropRef}
    >
      <div className="image-layer-content" style={{ zIndex: imageElement.layer }}>
        {!!imageElement.image_id && shouldColorizeImage && (
          <div ref={previewRef} style={imageStyles}>
            <RecoloredImage
              className="image-layer-preview"
              src={imageURL}
              color={linkedElement.font_color}
              width={width}
              height={height}
            />
          </div>
        )}
        {!!imageElement.image_id && !shouldColorizeImage && (
          <img
            className="image-layer-preview"
            style={imageStyles}
            src={imageURL}
            alt="init Customizable Pic"
            draggable="false"
            ref={previewRef}
          />
        )}
        {!imageElement.image_id && <span className="placeholder-image" ref={previewRef} />}
        {controlsEnabled && <div className="image-layer-overlay controls" />}
        {isDragging && <div className="image-layer-overlay isDragging" />}
        {isOver && <div className="image-layer-overlay isOver" />}
        {canDrop && <div className="image-layer-overlay canDrop" />}
        {isBusy && (
          <div className="image-layer-overlay isBusy">
            <Loader />
          </div>
        )}
      </div>
      {controlsEnabled && (
        <div className={cx('image-layer-controls', { isDragging })}>
          {!!onImageSave && (
            <ImageUploader
              imageElement={imageElement}
              onUploadComplete={handleUploadComplete}
              inputId={`${contentKey}_image_uploader`}
              analyticsProduct="CARDS"
              analyticsLocation="CUSTOMIZATION"
              renderButton={(busy, editable, handleUpload) => (
                <div className="image-layer-button" onClick={handleUpload}>
                  <i className="zolaicon zolaicon-upload-file" />
                  <span>
                    {(busy && 'Uploading') || (!imageElement.image_id && 'Upload') || 'Edit'}
                  </span>
                </div>
              )}
            />
          )}
          {!!onImageReorder && hasUploadcareImage && !detectTouchScreen() && (
            <div className="image-layer-button draggable" ref={dragRef}>
              <i className="zolaicon zolaicon-move" />
              <span>Reorder</span>
            </div>
          )}
          {!!onImageDelete && !!imageElement.image_id && (
            <div className="image-layer-button" onClick={handleDelete}>
              <i className="zolaicon zolaicon-trash" />
              <span>Delete</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

ImageLayer.propTypes = {
  allowPlaceholder: PropTypes.bool,
  controlsEnabled: PropTypes.bool,
  customizationDeleted: PropTypes.bool,
  hasUploadcareImage: PropTypes.bool,
  imageElement: PropTypes.shape({}),
  isFlipped: PropTypes.bool,
  isFoldable: PropTypes.bool,
  onImageDelete: PropTypes.func,
  onImageSave: PropTypes.func,
  onImageReorder: PropTypes.func,
  pageHeight: PropTypes.number,
  pageWidth: PropTypes.number,
  setActiveField: PropTypes.func,
  pageOrientation: PropTypes.string,
};

const mapDispatchToProps = dispatch => ({
  setActiveField: name => dispatch(projectActions.setActiveField(name)),
});

export default connect(null, mapDispatchToProps)(ImageLayer);
